import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { Link } from "react-router-dom";
import { governanceService } from "@/services/governance.service";
import type { PlatformUserAdmin, PlatformUserRole } from "@/types";
import toast from "react-hot-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { CreatePlatformUserModal } from "@/components/governance/CreatePlatformUserModal";

const ROLE_OPTIONS: PlatformUserRole[] = [
  "SUPPORT_ADMIN",
  "SUPPORT_AGENT",
  "CHANNEL_PARTNER",
];

const READONLY_ROLES: PlatformUserRole[] = ["SUPER_ADMIN"];

function RoleManagementPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = usePermissions();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<PlatformUserRole | "">("");
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const limit = 10;

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      role: role || undefined,
    }),
    [limit, page, role, search],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-platform-users", queryParams],
    queryFn: () => governanceService.listPlatformUsers(queryParams),
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { role?: PlatformUserRole; isActive?: boolean };
    }) => governanceService.updatePlatformUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-platform-users"] });
      toast.success("User updated");
    },
  });

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          title="Role management"
          subtitle="Assign platform roles and disable accounts."
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-slate-800"
                >
                  New user
                </button>
              )}
              <Link
                to="/audit"
                className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
              >
                View audit logs
              </Link>
            </div>
          }
        />
      )}

      {showCreateModal && (
        <CreatePlatformUserModal onClose={() => setShowCreateModal(false)} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search by name or email"
          className="h-10 flex-1 rounded-xl border border-slate-200 px-4 text-sm focus:border-slate-400 focus:outline-none"
        />
        <select
          value={role}
          onChange={(event) => {
            setRole(event.target.value as PlatformUserRole | "");
            setPage(1);
          }}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
        >
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={4}>
                  Loading users...
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((user) => (
                <RoleRow
                  key={user.id}
                  user={user}
                  onSave={mutateAsync}
                  isSaving={isPending}
                  canEdit={isSuperAdmin}
                />
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={4}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

type RoleRowProps = {
  user: PlatformUserAdmin;
  onSave: (payload: {
    id: string;
    payload: { role?: PlatformUserRole; isActive?: boolean };
  }) => Promise<void>;
  isSaving: boolean;
  canEdit: boolean;
};

function RoleRow({ user, onSave, isSaving, canEdit }: RoleRowProps) {
  const [role, setRole] = useState<PlatformUserRole>(user.role);
  const [active, setActive] = useState<boolean>(user.isActive);

  const isReadOnly = READONLY_ROLES.includes(user.role);
  const canEditThisUser = canEdit && !isReadOnly;

  return (
    <tr className="border-b border-slate-100 last:border-none">
      <td className="px-4 py-3 text-sm text-slate-600">
        <p className="font-semibold text-slate-900">
          {user.firstName} {user.lastName ?? ""}
        </p>
        <p className="text-xs text-slate-500">{user.email}</p>
      </td>
      <td className="px-4 py-3">
        <select
          value={role}
          onChange={(event) => setRole(event.target.value as PlatformUserRole)}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm disabled:opacity-50"
          disabled={!canEditThisUser}
        >
          {isReadOnly ? (
            <option value={user.role}>{user.role}</option>
          ) : (
            ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))
          )}
        </select>
      </td>
      <td className="px-4 py-3">
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            disabled={!canEditThisUser}
          />
          {active ? "Active" : "Inactive"}
        </label>
      </td>
      <td className="px-4 py-3 text-right">
        {canEditThisUser ? (
          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              onSave({ id: user.id, payload: { role, isActive: active } })
            }
            className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white"
          >
            Save
          </button>
        ) : (
          <span className="text-xs text-slate-400">
            {isReadOnly ? "Protected" : "View only"}
          </span>
        )}
      </td>
    </tr>
  );
}

export default RoleManagementPage;
